import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity, AlertCircle, BarChart3, Briefcase, ChevronLeft, ChevronRight, Clock, Flame, Gauge, Hammer, Info,
  ListChecks, RefreshCw, Search, Sparkles, TrendingUp, Trophy, Users, X, Zap,
} from 'lucide-react';
import {
  apiService, type JobGainersResponse, type JobLevelsResponse, type JobMethodsResponse, type JobObjective,
  type JobsOverviewResponse, type JobTopResponse, type JobTrendsResponse, type PackGroupDTO,
} from '@/services/api.service';
import { projectMatches } from '@/config/games';
import { useGameStore } from '@/stores/game.store';
import { useActiveGameGroup } from '@/stores/gameGroups.store';
import { formatPlayerName } from '@/utils/playerName';
import { PAGE_SIZES, SEARCH_DELAY_MS, medal, pageLabel, shortUuid } from './moneyLeaderboard';
import {
  type LevelCurves, curveOf, curveParams, curvesFromJobLevels, curvesFromLevelSettings, dayLabel,
  formatInstant, formatPercent, formatXp, formatXpExact, hourLabel, jobColor, jobLabel, jobsError, kindLabel,
  levelOf, shortDay, sortJobs, targetLabel,
} from './jobStats';
import { BarList, ColumnChart, type ColumnDatum } from './JobsCharts';
import lb from './MoneyLeaderboardSection.module.scss';
import styles from './JobsStatsSection.module.scss';

const PERIODS = [7, 14, 30, 90] as const;

/** Lignes du classement par défaut : la section en porte plusieurs autres en dessous. */
const TOP_PAGE_SIZE = 25;

/** Mod de métiers du projet et section de sa courbe de niveaux dans jobs.config.json. */
function levelSource(gameId: string): { mod: 'JobsMod' | 'RecubeJobsMod'; key: string } {
  return gameId === 'paladium' ? { mod: 'JobsMod', key: 'JobLevels' } : { mod: 'RecubeJobsMod', key: 'LevelSettings' };
}

function playerLabel(row: { uuid: string; name?: string; displayName?: string }): string {
  return formatPlayerName(row.name, row.displayName, shortUuid(row.uuid));
}

/**
 * Section « Métiers » de l'espace Statistiques, pour Paladium (JobsMod) et NationsGlory (RecubeJobsMod, par
 * groupe de jeu) : XP par métier, niveaux, classement, tendances, plus gros gagnants et méthodes. Tout est lu par
 * le satellite du projet, en lecture seule ; les tendances viennent de son relevé horaire.
 */
export function JobsStatsSection() {
  const gameId = useGameStore((s) => s.activeGameId);
  const group = useActiveGameGroup();
  const groupKey = group?.key ?? null;

  const [overview, setOverview] = useState<JobsOverviewResponse | null>(null);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  /** Code HTTP de l'échec : 404 = satellite d'avant la 1.0.25, sans les routes des métiers. */
  const [overviewStatus, setOverviewStatus] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState<number>(14);
  const [trends, setTrends] = useState<JobTrendsResponse | null>(null);
  const [trendsError, setTrendsError] = useState<string | null>(null);
  const levelCurves = useLevelCurves(gameId);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    const res = await apiService.getJobsOverview();
    if (res.data) {
      setOverview(res.data);
      setOverviewError(null);
      setOverviewStatus(undefined);
    } else {
      setOverview(null);
      setOverviewError(jobsError(res.status, res.error));
      setOverviewStatus(res.status);
    }
    setLoading(false);
  }, [gameId, groupKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void loadOverview(); }, [loadOverview]);

  useEffect(() => {
    let alive = true;
    setTrends(null);
    void apiService.getJobTrends(days).then((res) => {
      if (!alive) return;
      if (res.data) { setTrends(res.data); setTrendsError(null); } else setTrendsError(jobsError(res.status, res.error));
    });
    return () => { alive = false; };
  }, [days, gameId, groupKey]);

  const jobs = useMemo(() => sortJobs((overview?.jobs ?? []).map((j) => j.jobId)), [overview]);
  const lastDay = trends?.series[trends.series.length - 1];
  const prevDay = trends?.series[trends.series.length - 2];
  const periodGained = (trends?.series ?? []).reduce((s, d) => s + d.gained, 0);

  if (loading && !overview && !overviewError) {
    return <div className={lb.state}><div className={lb.spinner} /><p>Lecture des métiers...</p></div>;
  }
  if (overviewError && !overview && overviewStatus === 404) {
    // Satellite d'avant la 1.0.25 : rien de cassé, la page se remplira une fois le satellite redéployé.
    return (
      <div className={lb.state}>
        <Info size={32} className={styles.stateIcon} />
        <h3>Statistiques des métiers pas encore servies</h3>
        <p className={styles.stateText}>
          Le satellite de ce projet{group ? ` (groupe ${group.label})` : ''} ne sert pas encore ces statistiques : elles
          arrivent avec le satellite 1.0.25. La page se remplira d'elle-même une fois le satellite redéployé.
        </p>
        <button className={lb.buttonGhost} onClick={() => void loadOverview()}>
          <RefreshCw size={16} /> Vérifier de nouveau
        </button>
      </div>
    );
  }
  if (overviewError && !overview) {
    return (
      <div className={lb.state}>
        <AlertCircle size={32} />
        <h3>Statistiques des métiers illisibles</h3>
        <p>{overviewError}</p>
        <button className={lb.button} onClick={() => void loadOverview()}>Réessayer</button>
      </div>
    );
  }
  if (overview && !overview.available) {
    return (
      <div className={lb.state}>
        <Briefcase size={32} />
        <h3>Pas de métiers ici</h3>
        <p>{overview.reason}</p>
      </div>
    );
  }
  if (!overview) return null;

  const top = [...overview.jobs].sort((a, b) => b.totalXp - a.totalXp)[0];
  const cards = [
    { icon: Users, value: overview.players.toLocaleString('fr-FR'), label: 'Joueurs avec un métier', sub: "Au moins un point d'XP" },
    { icon: Sparkles, value: formatXp(overview.totalXp), label: 'XP cumulée', sub: 'Tous métiers, depuis le début' },
    { icon: Trophy, value: top ? jobLabel(top.jobId) : '-', label: 'Métier le plus monté', sub: top ? `${formatPercent(top.share)} de toute l'XP` : '' },
    {
      icon: TrendingUp,
      value: trends?.tracking.since ? formatXp(periodGained) : '-',
      label: `XP gagnée sur ${days} jours`,
      sub: trends?.tracking.since ? `depuis le ${formatInstant(trends.tracking.since)}` : 'Suivi pas encore commencé',
    },
    {
      icon: Activity,
      value: lastDay ? lastDay.players.toLocaleString('fr-FR') : '-',
      label: "Joueurs actifs aujourd'hui",
      sub: prevDay ? `${prevDay.players.toLocaleString('fr-FR')} hier` : 'Ont gagné de l\'XP de métier',
    },
    {
      icon: Zap,
      value: (overview.boosts?.activePlayers ?? 0).toLocaleString('fr-FR'),
      label: "Boosts d'XP actifs",
      sub: overview.boosts && overview.boosts.byName.length > 0
        ? overview.boosts.byName.slice(0, 2).map((b) => `${b.name} : ${b.players}`).join(', ')
        : 'Aucune potion en cours',
    },
  ];

  return (
    <div className={styles.section}>
      <div className={lb.cards}>
        {cards.map((c) => (
          <div key={c.label} className={lb.card}>
            <div className={lb.cardIcon}><c.icon size={22} strokeWidth={2} /></div>
            <div className={lb.cardText}>
              <span className={lb.cardValue}>{c.value}</span>
              <span className={lb.cardLabel}>{c.label}</span>
              <span className={lb.cardSub}>{c.sub}</span>
            </div>
          </div>
        ))}
      </div>

      <TrackingNotice overview={overview} groupLabel={group?.label ?? null} />

      <div className={styles.grid2}>
        <XpByJob overview={overview} />
        <LevelsBlock gameId={gameId} groupKey={groupKey} jobs={jobs} level={levelCurves} />
      </div>

      <TrendsBlock trends={trends} error={trendsError} days={days} setDays={setDays} jobs={jobs} />

      <LeaderboardBlock jobs={jobs} gameId={gameId} groupKey={groupKey} curves={levelCurves.curves} />

      <GainersBlock days={days} jobs={jobs} gameId={gameId} groupKey={groupKey} tracking={!!trends?.tracking.since} />

      <MethodsBlock days={days} jobs={jobs} gameId={gameId} groupKey={groupKey} overview={overview} />

      <QuestsBlock overview={overview} />
    </div>
  );
}

// ==================== Suivi ====================

function TrackingNotice({ overview, groupLabel }: { overview: JobsOverviewResponse; groupLabel: string | null }) {
  const t = overview.tracking;
  const group = groupLabel ? ` Chiffres du groupe ${groupLabel}, lus dans la base des métiers de son satellite.` : '';
  if (!t.enabled) {
    return (
      <div className={styles.info}><Info size={16} />
        <span>Le relevé horaire est coupé dans la config de ce satellite (job-xp-tracker) : pas de tendances.{group}</span>
      </div>
    );
  }
  if (!t.since) {
    return (
      <div className={styles.info}><Clock size={16} />
        <span>
          Le suivi des tendances commence au premier relevé, à la prochaine heure pile : il pose l'état de départ, et
          l'XP gagnée apparaît à partir du relevé suivant. L'XP accumulée avant n'a pas de date.{group}
        </span>
      </div>
    );
  }
  return (
    <div className={styles.info}><Clock size={16} />
      <span>
        Tendances suivies depuis le {formatInstant(t.since)}, relevé toutes les heures
        {t.lastRun ? ` (dernier le ${formatInstant(t.lastRun)})` : ''}, gardées {t.retentionDays} jours.
        XP, niveaux et classement sont lus en direct.{group}
      </span>
    </div>
  );
}

// ==================== XP par métier ====================

function XpByJob({ overview }: { overview: JobsOverviewResponse }) {
  const ids = overview.jobs.map((j) => j.jobId);
  const rows = [...overview.jobs].sort((a, b) => b.totalXp - a.totalXp);
  return (
    <section className={styles.block}>
      <div className={styles.blockHead}>
        <h3><BarChart3 size={18} /> Répartition de l'XP par métier</h3>
      </div>
      <p className={styles.blockSub}>Part de toute l'XP cumulée, et joueurs qui ont de l'XP dans chaque métier.</p>
      {rows.length === 0 ? <p className={lb.empty}>Aucun joueur n'a encore d'XP de métier.</p> : (
        <BarList
          formatValue={formatXp}
          rows={rows.map((j) => ({
            id: j.jobId,
            label: jobLabel(j.jobId),
            color: jobColor(j.jobId, ids),
            value: j.totalXp,
            share: j.share,
            sub: `${formatPercent(j.share)} · ${j.players.toLocaleString('fr-FR')} joueurs`,
          }))}
        />
      )}
      {rows.length > 0 && (
        <div className={lb.tableWrap}>
          <table className={lb.table}>
            <thead>
              <tr><th>Métier</th><th className={lb.num}>Joueurs</th><th className={lb.num}>Moyenne</th><th className={lb.num}>Médiane</th><th className={lb.num}>Record</th></tr>
            </thead>
            <tbody>
              {rows.map((j) => (
                <tr key={j.jobId}>
                  <td><span className={styles.jobCell}><span className={styles.swatch} style={{ background: jobColor(j.jobId, ids) }} />{jobLabel(j.jobId)}</span></td>
                  <td className={lb.num}>{j.players.toLocaleString('fr-FR')}</td>
                  <td className={lb.num}>{formatXp(j.averageXp)}</td>
                  <td className={lb.num}>{formatXp(j.medianXp)}</td>
                  <td className={lb.num}>{formatXp(j.topXp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ==================== Niveaux ====================

/** Courbe de niveaux du projet, lue dans la config de l'image ; null si illisible (droits, panel ancien). */
function useLevelCurves(gameId: string): LevelCurvesState {
  const [state, setState] = useState<LevelCurvesState>({ curves: null, why: null, source: null });
  useEffect(() => {
    let alive = true;
    setState({ curves: null, why: null, source: null });
    const src = levelSource(gameId);
    void apiService.inspectContainerConfig({ fileNames: ['jobs.config.json'], keys: [src.key], mod: src.mod }).then((res) => {
      if (!alive) return;
      const section = res.data?.sections?.[src.key];
      if (res.data?.success && section !== undefined) {
        const curves = src.mod === 'JobsMod' ? curvesFromJobLevels(section) : curvesFromLevelSettings(section);
        setState({ curves, why: null, source: res.data.referenceContainerName });
      } else if (res.data?.success && src.mod === 'RecubeJobsMod') {
        // Pas de LevelSettings : le mod garde sa courbe d'origine.
        setState({ curves: curvesFromLevelSettings(undefined), why: null, source: res.data.referenceContainerName });
      } else {
        setState({ curves: null, why: res.data?.message ?? res.error ?? 'courbe illisible', source: null });
      }
    });
    return () => { alive = false; };
  }, [gameId]);
  return state;
}

type LevelCurvesState = { curves: LevelCurves | null; why: string | null; source: string | null };

function LevelsBlock({ gameId, groupKey, jobs, level }: {
  gameId: string; groupKey: string | null; jobs: string[]; level: LevelCurvesState;
}) {
  const { curves, why, source } = level;
  const [data, setData] = useState<JobLevelsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    // La courbe d'abord (ou son échec), puis les niveaux : sans courbe, tranches d'XP.
    if (curves === null && why === null) return;
    void apiService.getJobLevels(curves ? curveParams(curves) : '').then((res) => {
      if (!alive) return;
      if (res.data) { setData(res.data); setError(null); } else setError(jobsError(res.status, res.error));
    });
    return () => { alive = false; };
  }, [curves, why, gameId, groupKey]);

  const current = data?.jobs.find((j) => j.jobId === (job ?? jobs[0])) ?? data?.jobs[0];
  const color = current ? jobColor(current.jobId, jobs) : 'var(--job-other)';
  const columns: ColumnDatum[] = current?.levels
    ? current.levels.map((l) => ({
      key: String(l.level),
      label: String(l.level),
      title: `Niveau ${l.level}${l.toXp ? ` (${formatXpExact(l.fromXp)} à ${formatXpExact(l.toXp - 1)} XP)` : ` (${formatXpExact(l.fromXp)} XP et plus)`}`,
      segments: [{ id: 'p', label: 'Joueurs', value: l.players, color }],
    }))
    : (current?.buckets ?? []).map((b) => ({
      key: String(b.fromXp),
      label: formatXp(b.fromXp),
      title: `${formatXpExact(b.fromXp)} à ${formatXpExact(b.toXp - 1)} XP`,
      segments: [{ id: 'p', label: 'Joueurs', value: b.players, color }],
    }));
  const maxLevelPlayers = current?.levels && current.maxLevel ? current.levels[current.maxLevel]?.players ?? 0 : 0;
  const average = current?.levels && current.players > 0
    ? current.levels.reduce((s, l) => s + l.level * l.players, 0) / current.players : null;

  return (
    <section className={styles.block}>
      <div className={styles.blockHead}>
        <h3><Gauge size={18} /> Répartition par niveau</h3>
      </div>
      <JobChips jobs={jobs} value={current?.jobId ?? null} onChange={setJob} />
      {error ? <div className={lb.errorBox}><AlertCircle size={16} /><span>{error}</span></div>
        : !data ? <div className={lb.state}><div className={lb.spinner} /></div>
          : !current ? <p className={lb.empty}>Aucun joueur n'a encore d'XP de métier.</p> : (
            <>
              <ColumnChart
                data={columns}
                formatValue={(n) => `${Math.round(n).toLocaleString('fr-FR')} joueur${n > 1 ? 's' : ''}`}
                formatTick={(n) => Math.round(n).toLocaleString('fr-FR')}
                ariaLabel={`Joueurs par niveau, ${jobLabel(current.jobId)}`}
                height={200}
              />
              <div className={styles.statLine}>
                <span><b>{current.players.toLocaleString('fr-FR')}</b> joueurs en {jobLabel(current.jobId).toLowerCase()}</span>
                {average !== null && <span>Niveau moyen <b>{average.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}</b></span>}
                {current.maxLevel !== undefined && <span>Au niveau {current.maxLevel} (max) : <b>{maxLevelPlayers.toLocaleString('fr-FR')}</b></span>}
              </div>
              <p className={styles.muted}>
                {current.levels
                  ? `Niveaux calculés avec la courbe de jobs.config.json${source ? ` (image ${source})` : ''}, comme en jeu.`
                  : `Courbe des niveaux illisible (${why ?? 'aucune courbe pour ce métier'}) : répartition en tranches d'XP.`}
              </p>
            </>
          )}
    </section>
  );
}

function JobChips({ jobs, value, onChange, withAll }: {
  jobs: string[]; value: string | null; onChange: (j: string | null) => void; withAll?: boolean;
}) {
  return (
    <div className={styles.chips} role="tablist">
      {withAll && (
        <button type="button" role="tab" aria-selected={value === null}
          className={`${styles.chip} ${value === null ? styles.chipActive : ''}`} onClick={() => onChange(null)}>
          Tous les métiers
        </button>
      )}
      {jobs.map((j) => (
        <button key={j} type="button" role="tab" aria-selected={value === j}
          className={`${styles.chip} ${value === j ? styles.chipActive : ''}`} onClick={() => onChange(j)}>
          <span className={styles.swatch} style={{ background: jobColor(j, jobs) }} />{jobLabel(j)}
        </button>
      ))}
    </div>
  );
}

// ==================== Tendances ====================

function TrendsBlock({ trends, error, days, setDays, jobs }: {
  trends: JobTrendsResponse | null; error: string | null; days: number; setDays: (d: number) => void; jobs: string[];
}) {
  const all = sortJobs([...new Set([...jobs, ...(trends?.jobs ?? [])])]);
  const legend = all.map((j) => ({ id: j, label: jobLabel(j), color: jobColor(j, all) }));
  const series = trends?.series ?? [];
  const daily: ColumnDatum[] = series.map((d) => ({
    key: d.date,
    label: shortDay(d.date),
    title: dayLabel(d.date),
    segments: all.map((j) => ({ id: j, label: jobLabel(j), value: d.jobs.find((x) => x.jobId === j)?.gained ?? 0, color: jobColor(j, all) })),
    note: `${d.players.toLocaleString('fr-FR')} joueurs actifs${d.newcomers > 0 ? `, dont ${d.newcomers} nouveaux` : ''}`,
  }));
  const players: ColumnDatum[] = series.map((d) => ({
    key: d.date,
    label: shortDay(d.date),
    title: dayLabel(d.date),
    segments: [{ id: 'p', label: 'Joueurs actifs', value: d.players, color: 'var(--color-primary)' }],
    note: d.newcomers > 0 ? `dont ${d.newcomers} à leur première XP de métier` : undefined,
  }));
  const hourDays = Math.max(1, trends?.recordedDays ?? 1);
  const hours: ColumnDatum[] = (trends?.hours ?? []).map((h) => ({
    key: String(h.hour),
    label: String(h.hour),
    title: `${hourLabel(h.hour)} à ${hourLabel((h.hour + 1) % 24)}, moyenne par jour`,
    segments: all.map((j) => ({ id: j, label: jobLabel(j), value: (h.byJob[j] ?? 0) / hourDays, color: jobColor(j, all) })),
  }));
  const peak = (trends?.hours ?? []).reduce<{ hour: number; gained: number } | null>((m, h) => (!m || h.gained > m.gained ? h : m), null);
  const best = series.reduce<(typeof series)[number] | null>((m, d) => (!m || d.gained > m.gained ? d : m), null);

  return (
    <section className={styles.block}>
      <div className={styles.blockHead}>
        <h3><TrendingUp size={18} /> Tendances</h3>
        <div className={styles.controls}>
          <select className={lb.select} value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Période">
            {PERIODS.map((p) => <option key={p} value={p}>{p} jours</option>)}
          </select>
        </div>
      </div>
      {error ? <div className={lb.errorBox}><AlertCircle size={16} /><span>{error}</span></div>
        : !trends ? <div className={lb.state}><div className={lb.spinner} /></div>
          : !trends.tracking.since || series.length === 0 ? (
            <p className={lb.empty}>Pas encore de tendances : elles commencent avec le relevé horaire du satellite.</p>
          ) : (
            <>
              <div className={styles.statLine}>
                <span>Meilleur jour : <b>{best ? `${dayLabel(best.date)}, ${formatXp(best.gained)} XP` : '-'}</b></span>
                <span>Heure de pointe : <b>{peak && peak.gained > 0 ? `${hourLabel(peak.hour)} à ${hourLabel((peak.hour + 1) % 24)}` : '-'}</b></span>
                <span>Nouveaux joueurs de métier : <b>{series.reduce((s, d) => s + d.newcomers, 0).toLocaleString('fr-FR')}</b></span>
              </div>
              <h4 className={styles.blockSub}>XP gagnée par jour et par métier</h4>
              <ColumnChart data={daily} formatValue={(n) => `${formatXp(n)} XP`} formatTick={formatXp} legend={legend}
                ariaLabel="XP gagnée par jour et par métier" />
              <div className={styles.grid2}>
                <div>
                  <h4 className={styles.blockSub}>Joueurs actifs par jour (au moins un gain d'XP)</h4>
                  <ColumnChart data={players} formatValue={(n) => `${Math.round(n).toLocaleString('fr-FR')} joueurs`}
                    formatTick={(n) => Math.round(n).toLocaleString('fr-FR')} ariaLabel="Joueurs actifs par jour" height={180} />
                </div>
                <div>
                  <h4 className={styles.blockSub}>Heures de pointe : XP moyenne par heure de la journée</h4>
                  <ColumnChart data={hours} formatValue={(n) => `${formatXp(n)} XP`} formatTick={formatXp} legend={legend}
                    ariaLabel="XP moyenne par heure de la journée" height={180} />
                </div>
              </div>
              <details className={styles.details}>
                <summary>Voir les chiffres jour par jour</summary>
                <div className={lb.tableWrap}>
                  <table className={lb.table}>
                    <thead>
                      <tr>
                        <th>Jour</th>
                        {all.map((j) => <th key={j} className={lb.num}>{jobLabel(j)}</th>)}
                        <th className={lb.num}>Total</th><th className={lb.num}>Joueurs</th><th className={lb.num}>XP retirée</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...series].reverse().map((d) => (
                        <tr key={d.date}>
                          <td className={lb.nowrap}>{dayLabel(d.date)}</td>
                          {all.map((j) => <td key={j} className={lb.num}>{formatXpExact(d.jobs.find((x) => x.jobId === j)?.gained ?? 0)}</td>)}
                          <td className={lb.num}><b>{formatXpExact(d.gained)}</b></td>
                          <td className={lb.num}>{d.players.toLocaleString('fr-FR')}</td>
                          <td className={lb.num}>{d.lost > 0 ? formatXpExact(d.lost) : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className={styles.muted}>« XP retirée » : XP enlevée par une commande de staff ou une remise à zéro ; elle ne compte pas dans les gains.</p>
              </details>
            </>
          )}
    </section>
  );
}

// ==================== Classement ====================

function LeaderboardBlock({ jobs, gameId, groupKey, curves }: {
  jobs: string[]; gameId: string; groupKey: string | null; curves: LevelCurves | null;
}) {
  const [job, setJob] = useState<string | null>(null);
  const [data, setData] = useState<JobTopResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState<number>(TOP_PAGE_SIZE);
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const requestId = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => { setQuery(input.trim()); setPage(0); }, SEARCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [input]);

  useEffect(() => { setPage(0); setData(null); }, [gameId, groupKey, job]);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    const res = await apiService.getJobLeaderboard(job, page, size, query);
    if (id !== requestId.current) return;
    if (res.data) { setData(res.data); setError(null); } else setError(jobsError(res.status, res.error));
    setLoading(false);
  }, [job, page, size, query, gameId, groupKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void load(); }, [load]);

  const rows = data?.players ?? [];
  const searching = !!data?.query;
  const totalPages = data?.totalPages ?? 1;
  const curve = job && curves ? curveOf(curves, job) : null;

  return (
    <section className={styles.block}>
      <div className={styles.blockHead}>
        <h3><Trophy size={18} /> Classement des joueurs {job ? `en ${jobLabel(job).toLowerCase()}` : '(tous métiers)'}</h3>
        <span className={lb.tableCount}>
          {searching ? `${rows.length} joueur${rows.length > 1 ? 's' : ''} trouvé${rows.length > 1 ? 's' : ''}` : pageLabel(page, totalPages)}
        </span>
      </div>
      <JobChips jobs={jobs} value={job} onChange={setJob} withAll />
      <div className={lb.toolbar}>
        <label className={lb.search}>
          <Search size={16} />
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Rechercher un pseudo, un gamertag ou un uuid"
            aria-label="Rechercher un joueur dans le classement des métiers" />
          {input && <button type="button" className={lb.clear} onClick={() => setInput('')} title="Effacer la recherche"><X size={14} /></button>}
        </label>
        <select className={lb.select} value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }} disabled={searching} title="Joueurs par page">
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} par page</option>)}
        </select>
        <button className={lb.buttonGhost} onClick={() => void load()} disabled={loading} title="Relire l'XP">
          <RefreshCw size={16} className={loading ? lb.spin : undefined} /> Actualiser
        </button>
      </div>
      {error && <div className={lb.errorBox}><AlertCircle size={16} /><span>{error}</span></div>}
      {data?.namesPartial && (
        <div className={lb.notice}><AlertCircle size={16} /><span>Certains joueurs n'ont pas encore de pseudo connu : leur uuid est affiché à la place.</span></div>
      )}
      {rows.length === 0 ? (
        <p className={lb.empty}>
          {searching ? "Aucun joueur trouvé avec de l'XP dans ce classement." : !data ? '' : "Aucun joueur n'a encore d'XP ici."}
        </p>
      ) : (
        <div className={lb.tableWrap}>
          <table className={lb.table}>
            <thead>
              <tr>
                <th>Rang</th>
                <th>Joueur</th>
                {job && curve && <th className={lb.num}>Niveau</th>}
                <th className={lb.num}>XP</th>
                {!job && <th>Métiers</th>}
                <th className={lb.num}>Part</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const m = medal(r.rank);
                return (
                  <tr key={r.uuid}>
                    <td><span className={`${lb.rank} ${m ? lb[m] : ''}`}>{r.rank.toLocaleString('fr-FR')}</span></td>
                    <td>
                      <div className={lb.player}>
                        <span className={r.online ? lb.dotOnline : lb.dotOffline} title={r.online ? 'En ligne' : 'Hors ligne'} />
                        <div className={lb.playerText}>
                          <span className={lb.playerName}>{playerLabel(r)}</span>
                          {r.online && r.serverName && <span className={lb.playerSub}>sur {r.serverName}</span>}
                        </div>
                      </div>
                    </td>
                    {job && curve && <td className={lb.num}><span className={styles.levelPill}>{levelOf(r.xp, curve)}</span></td>}
                    <td className={lb.num} title={`${formatXpExact(r.xp)} XP`}>{formatXp(r.xp)}</td>
                    {!job && (
                      <td>
                        <div className={styles.miniJobs}>
                          {sortJobs(Object.keys(r.jobs)).map((j) => {
                            const c = curves ? curveOf(curves, j) : null;
                            return (
                              <span key={j} title={`${formatXpExact(r.jobs[j] ?? 0)} XP`}>
                                <span className={styles.swatch} style={{ background: jobColor(j, jobs) }} />
                                {jobLabel(j)} {c ? `niv. ${levelOf(r.jobs[j] ?? 0, c)}` : formatXp(r.jobs[j] ?? 0)}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                    )}
                    <td className={lb.num}>{formatPercent(r.share)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!searching && totalPages > 1 && (
        <div className={lb.pagination}>
          <button className={lb.buttonGhost} onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={loading || page === 0}><ChevronLeft size={16} /> Précédent</button>
          <span>{pageLabel(page, totalPages)}</span>
          <button className={lb.buttonGhost} onClick={() => setPage((p) => p + 1)} disabled={loading || page + 1 >= totalPages}>Suivant <ChevronRight size={16} /></button>
        </div>
      )}
      <p className={lb.hint}>
        XP lue en direct dans la base des métiers (mise à jour par le jeu toutes les minutes). « Tous les métiers » classe la
        somme de l'XP ; la part est celle de toute l'XP du classement. Deux XP égales ont le même rang.
      </p>
    </section>
  );
}

// ==================== Plus gros gagnants ====================

function GainersBlock({ days, jobs, gameId, groupKey, tracking }: {
  days: number; jobs: string[]; gameId: string; groupKey: string | null; tracking: boolean;
}) {
  const [job, setJob] = useState<string | null>(null);
  const [data, setData] = useState<JobGainersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    void apiService.getJobGainers(days, job, 20).then((res) => {
      if (!alive) return;
      if (res.data) { setData(res.data); setError(null); } else setError(jobsError(res.status, res.error));
    });
    return () => { alive = false; };
  }, [days, job, gameId, groupKey]);

  const rows = data?.players ?? [];
  return (
    <section className={styles.block}>
      <div className={styles.blockHead}>
        <h3><Flame size={18} /> Plus gros gagnants sur {days} jours</h3>
      </div>
      <JobChips jobs={jobs} value={job} onChange={setJob} withAll />
      {error ? <div className={lb.errorBox}><AlertCircle size={16} /><span>{error}</span></div>
        : !data ? <div className={lb.state}><div className={lb.spinner} /></div>
          : rows.length === 0 ? (
            <p className={lb.empty}>{tracking ? "Personne n'a gagné d'XP sur la période." : 'Pas encore de relevé : les gains apparaîtront avec le suivi.'}</p>
          ) : (
            <div className={lb.tableWrap}>
              <table className={lb.table}>
                <thead>
                  <tr><th>Rang</th><th>Joueur</th><th className={lb.num}>XP gagnée</th><th className={lb.num}>Jours actifs</th><th>Détail</th></tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const m = medal(r.rank);
                    return (
                      <tr key={r.uuid}>
                        <td><span className={`${lb.rank} ${m ? lb[m] : ''}`}>{r.rank}</span></td>
                        <td>
                          <div className={lb.player}>
                            <span className={r.online ? lb.dotOnline : lb.dotOffline} title={r.online ? 'En ligne' : 'Hors ligne'} />
                            <span className={lb.playerName}>{playerLabel(r)}</span>
                          </div>
                        </td>
                        <td className={lb.num} title={`${formatXpExact(r.gained)} XP`}>{formatXp(r.gained)}</td>
                        <td className={lb.num}>{r.activeDays} / {days}</td>
                        <td>
                          <div className={styles.miniJobs}>
                            {sortJobs(Object.keys(r.byJob)).map((j) => (
                              <span key={j}><span className={styles.swatch} style={{ background: jobColor(j, jobs) }} />{jobLabel(j)} {formatXp(r.byJob[j] ?? 0)}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
    </section>
  );
}

// ==================== Méthodes ====================

/** Barème du pack de métiers : XP rapportée par chaque action, lu dans les objectifs du behavior pack. */
function useBareme(gameId: string): JobObjective[] | null {
  const [rows, setRows] = useState<JobObjective[] | null>(null);
  useEffect(() => {
    let alive = true;
    void (async () => {
      const gr = await apiService.getPacksGroups();
      const groups = (gr.data?.groups ?? []).filter((g: PackGroupDTO) => projectMatches(g.projects, gameId));
      const per = await Promise.all(groups.map(async (g: PackGroupDTO) => (await apiService.getJobObjectives(g.id)).data?.objectives ?? []));
      if (alive) setRows(per.flat());
    })();
    return () => { alive = false; };
  }, [gameId]);
  return rows;
}

function MethodsBlock({ days, jobs, gameId, groupKey, overview }: {
  days: number; jobs: string[]; gameId: string; groupKey: string | null; overview: JobsOverviewResponse;
}) {
  const [job, setJob] = useState<string | null>(null);
  const [data, setData] = useState<JobMethodsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const bareme = useBareme(gameId);
  const journal = overview.methods?.available ?? false;

  useEffect(() => {
    let alive = true;
    if (!journal) return;
    setData(null);
    void apiService.getJobMethods(days, job).then((res) => {
      if (!alive) return;
      if (res.data) { setData(res.data); setError(null); } else setError(jobsError(res.status, res.error));
    });
    return () => { alive = false; };
  }, [days, job, gameId, groupKey, journal]);

  // Les types d'action sont écrits à côté de chaque barre : une seule couleur, pour ne pas les confondre avec les
  // couleurs des métiers de la même page.
  const kindColor = () => 'var(--color-primary)';
  const scale = useMemo(() => (bareme ?? [])
    .filter((o) => o.job && o.experience && (!job || o.job === job))
    .sort((a, b) => (b.experience ?? 0) - (a.experience ?? 0)), [bareme, job]);

  return (
    <section className={styles.block}>
      <div className={styles.blockHead}>
        <h3><Hammer size={18} /> Méthodes : ce qui rapporte l'XP</h3>
      </div>
      <JobChips jobs={jobs} value={job} onChange={setJob} withAll />
      {!journal ? (
        <div className={styles.info}><Info size={16} />
          <span>
            Le jeu ne note pas encore d'où vient l'XP (casser, tuer, fabriquer, cuire...). Ce suivi arrive avec la prochaine
            version des mods de métiers (JobsMod 0.0.48 sur Paladium, RecubeJobsMod 0.0.12 sur NationsGlory). En attendant,
            voici le barème du pack : l'XP que rapporte chaque action.
          </span>
        </div>
      ) : error ? <div className={lb.errorBox}><AlertCircle size={16} /><span>{error}</span></div>
        : !data ? <div className={lb.state}><div className={lb.spinner} /></div>
          : data.kinds.length === 0 ? <p className={lb.empty}>Aucun gain d'XP noté sur la période.</p> : (
            <>
              <p className={styles.blockSub}>
                {formatXp(data.totalXp)} XP en {data.totalActions.toLocaleString('fr-FR')} actions sur {days} jours
                {data.since ? `, suivi depuis le ${formatInstant(data.since)}` : ''}.
              </p>
              <div className={styles.grid2}>
                <div>
                  <h4 className={styles.blockSub}>Par type d'action</h4>
                  <BarList formatValue={(n) => `${formatXp(n)} XP`} rows={data.kinds.map((k) => ({
                    id: k.kind, label: kindLabel(k.kind), color: kindColor(), value: k.xp, share: k.share,
                    sub: `${formatPercent(k.share)} · ${k.count.toLocaleString('fr-FR')} actions`,
                  }))} />
                  {data.servers.length > 1 && (
                    <>
                      <h4 className={styles.blockSub} style={{ marginTop: 16 }}>Par serveur</h4>
                      <BarList formatValue={(n) => `${formatXp(n)} XP`} rows={data.servers.map((s) => ({
                        id: s.server, label: s.server, color: 'var(--color-primary)', value: s.xp,
                        share: data.totalXp > 0 ? (s.xp * 100) / data.totalXp : 0,
                        sub: `${s.count.toLocaleString('fr-FR')} actions`,
                      }))} />
                    </>
                  )}
                </div>
                <div>
                  <h4 className={styles.blockSub}>Actions qui rapportent le plus</h4>
                  <div className={lb.tableWrap}>
                    <table className={lb.table}>
                      <thead><tr><th>Action</th><th>Cible</th><th>Métier</th><th className={lb.num}>XP</th><th className={lb.num}>Fois</th></tr></thead>
                      <tbody>
                        {data.targets.slice(0, 15).map((t) => (
                          <tr key={`${t.kind}|${t.target}|${t.jobId}`}>
                            <td className={lb.nowrap}>{kindLabel(t.kind)}</td>
                            <td title={t.target}>{targetLabel(t.target)}</td>
                            <td><span className={styles.jobCell}><span className={styles.swatch} style={{ background: jobColor(t.jobId, jobs) }} />{jobLabel(t.jobId)}</span></td>
                            <td className={lb.num}>{formatXp(t.xp)}</td>
                            <td className={lb.num}>{t.count.toLocaleString('fr-FR')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
      {!journal && (
        bareme === null ? <div className={lb.state}><div className={lb.spinner} /></div>
          : scale.length === 0 ? <p className={lb.empty}>Barème illisible : aucun objectif de métier trouvé dans les packs du projet (ou pas le droit de lire les packs).</p> : (
            <div className={lb.tableWrap}>
              <table className={lb.table}>
                <thead><tr><th>Action</th><th>Cible</th><th>Métier</th><th className={lb.num}>XP par action</th><th className={lb.num}>Niveau requis</th></tr></thead>
                <tbody>
                  {scale.slice(0, 25).map((o) => (
                    <tr key={o.path}>
                      <td className={lb.nowrap}>{kindLabel(o.kind)}</td>
                      <td title={o.target ?? ''}>{targetLabel(o.target)}</td>
                      <td><span className={styles.jobCell}><span className={styles.swatch} style={{ background: jobColor(o.job ?? '', jobs) }} />{jobLabel(o.job)}</span></td>
                      <td className={lb.num}>{formatXpExact(o.experience ?? 0)}</td>
                      <td className={lb.num}>{o.levelNeeded ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {scale.length > 25 && <p className={styles.muted}>Les 25 actions qui rapportent le plus, sur {scale.length}. Tout le barème : Configuration &gt; Jobs.</p>}
            </div>
          )
      )}
    </section>
  );
}

// ==================== Quêtes du jour ====================

function QuestsBlock({ overview }: { overview: JobsOverviewResponse }) {
  const q = overview.quests;
  if (!q || (q.byType.length === 0 && q.players === 0)) return null;
  const assigned = q.byType.reduce((s, x) => s + x.assigned, 0);
  const done = q.byType.reduce((s, x) => s + x.completed, 0);
  return (
    <section className={styles.block}>
      <div className={styles.blockHead}>
        <h3><ListChecks size={18} /> Quêtes du jour</h3>
        <span className={lb.tableCount}>jour du {shortDay(q.day)} (UTC)</span>
      </div>
      <div className={styles.statLine}>
        <span><b>{q.players.toLocaleString('fr-FR')}</b> joueurs ont des quêtes</span>
        <span><b>{done.toLocaleString('fr-FR')}</b> réussies sur {assigned.toLocaleString('fr-FR')} ({formatPercent(assigned > 0 ? (done * 100) / assigned : 0)})</span>
        <span><b>{formatXp(q.byType.reduce((s, x) => s + x.xpCompleted, 0))}</b> XP de récompense gagnée</span>
      </div>
      <div className={lb.tableWrap}>
        <table className={lb.table}>
          <thead><tr><th>Type</th><th className={lb.num}>Attribuées</th><th className={lb.num}>Réussies</th><th className={lb.num}>Taux</th><th className={lb.num}>XP gagnée</th></tr></thead>
          <tbody>
            {q.byType.map((t) => (
              <tr key={t.key}>
                <td>{kindLabel(t.key)}</td>
                <td className={lb.num}>{t.assigned.toLocaleString('fr-FR')}</td>
                <td className={lb.num}>{t.completed.toLocaleString('fr-FR')}</td>
                <td className={lb.num}>{formatPercent(t.assigned > 0 ? (t.completed * 100) / t.assigned : 0)}</td>
                <td className={lb.num}>{formatXp(t.xpCompleted)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

